import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../lib/supabase';
import { supabaseUpload } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../components/ui/select';
import { 
  Plus, Clock, Coins, Tag, Upload, X, Image as ImageIcon, Sparkles, AlertCircle,
  Filter, ArrowUpDown, ChevronLeft, ChevronRight, SlidersHorizontal, Calendar,
  TrendingUp, TrendingDown, Search, ChevronDown, FolderTree, Check, AlertTriangle,
  Gift, Heart, Cake, Baby, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Budget options - store all as custom:amount format for DB compatibility
const DEFAULT_BUDGET_OPTIONS = [
  { value: '0-50', label: '€0 - €50', description: 'Piccoli oggetti' },
  { value: '50-100', label: '€50 - €100', description: 'Accessori semplici' },
  { value: '100-250', label: '€100 - €250', description: 'Creazioni base' },
  { value: '250-500', label: '€250 - €500', description: 'Pezzi elaborati' },
  { value: '500-1000', label: '€500 - €1.000', description: 'Lavori complessi' },
  { value: '1000-2000', label: '€1.000 - €2.000', description: 'Pezzi unici' },
  { value: '2000-5000', label: '€2.000 - €5.000', description: 'Alto artigianato' },
  { value: '5000+', label: '€5.000+', description: 'Lusso e collezioni' },
  { value: 'custom', label: 'Budget esatto', description: 'Indica tu l\'importo' },
];

// Timeline options
const DEFAULT_TIMELINE_OPTIONS = [
  { value: '1week', label: '1 Settimana', description: 'Urgente' },
  { value: '2weeks', label: '2 Settimane', description: 'Prioritario' },
  { value: '1month', label: '1 Mese', description: 'Standard' },
  { value: '2months', label: '2 Mesi', description: 'Con calma' },
  { value: '3months', label: '3 Mesi', description: 'Senza fretta' },
  { value: '6months', label: '6 Mesi', description: 'Progetto lungo' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
  { value: 'custom', label: 'Data specifica', description: 'Indica una data' },
];

// Gift event options
const GIFT_EVENTS = [
  { value: 'compleanno', label: 'Compleanno' },
  { value: 'matrimonio', label: 'Matrimonio' },
  { value: 'anniversario', label: 'Anniversario' },
  { value: 'battesimo', label: 'Battesimo' },
  { value: 'laurea', label: 'Laurea' },
  { value: 'nascita', label: 'Nascita' },
  { value: 'natale', label: 'Natale' },
  { value: 'san_valentino', label: 'San Valentino' },
  { value: 'festa_mamma', label: 'Festa della mamma' },
  { value: 'festa_papa', label: 'Festa del papà' },
  { value: 'pensionamento', label: 'Pensionamento' },
  { value: 'nuovo_lavoro', label: 'Nuovo lavoro' },
  { value: 'nuova_casa', label: 'Nuova casa' },
  { value: 'altro', label: 'Altro evento' },
];

// Gift recipient options
const GIFT_RECIPIENTS = [
  { value: 'uomo', label: 'Per un uomo' },
  { value: 'donna', label: 'Per una donna' },
  { value: 'coppia', label: 'Per una coppia' },
  { value: 'neonato', label: 'Per un neonato' },
  { value: 'bambino', label: 'Per un bambino/a' },
  { value: 'mamma', label: 'Per mia mamma' },
  { value: 'papa', label: 'Per mio papà' },
  { value: 'nonni', label: 'Per i nonni' },
  { value: 'amico', label: 'Per un amico/a' },
  { value: 'collega', label: 'Per un collega' },
  { value: 'me_stesso', label: 'Per me stesso' },
  { value: 'altro', label: 'Altro' },
];

export default function RequestsPage() {
  const { user, profile } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Check if we should open the create dialog from URL params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('create') === 'true') {
      setIsDialogOpen(true);
      // Clean up the URL
      navigate('/requests', { replace: true });
    }
  }, [location.search, navigate]);
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [customBudget, setCustomBudget] = useState('');
  const [timeline, setTimeline] = useState('');
  const [customDate, setCustomDate] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  
  // Gift-specific fields
  const [isGift, setIsGift] = useState(false);
  const [giftEvent, setGiftEvent] = useState('');
  const [giftRecipient, setGiftRecipient] = useState('');

  // Dynamic categories state
  const [dbCategories, setDbCategories] = useState([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [selectedCategoryPath, setSelectedCategoryPath] = useState([]);

  // Dynamic options state (from DB)
  const [budgetOptions, setBudgetOptions] = useState(DEFAULT_BUDGET_OPTIONS);
  const [timelineOptions, setTimelineOptions] = useState(DEFAULT_TIMELINE_OPTIONS);

  // Filter state
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriceRange, setFilterPriceRange] = useState('all');
  const [filterTimeline, setFilterTimeline] = useState('all');
  const [filterExpiry, setFilterExpiry] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Proposal state
  const [isProposalDialogOpen, setIsProposalDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [proposalMessage, setProposalMessage] = useState('');
  const [proposalPrice, setProposalPrice] = useState('');
  const [proposalTimeline, setProposalTimeline] = useState('1month');
  const [submittingProposal, setSubmittingProposal] = useState(false);

  // Fetch categories from database - only active categories for the filter
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Use /public endpoint to get only active categories
        const response = await fetch(`${API_URL}/api/categories/public`);
        if (response.ok) {
          const data = await response.json();
          if (data.categories) {
            setDbCategories(data.categories);
          }
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch budget and timeline options from database
  useEffect(() => {
    const fetchRequestOptions = async () => {
      try {
        const response = await fetch(`${API_URL}/api/request-options/public`);
        if (response.ok) {
          const data = await response.json();
          if (data.budget_options?.length > 0) {
            setBudgetOptions(data.budget_options);
          }
          if (data.timeline_options?.length > 0) {
            setTimelineOptions(data.timeline_options);
          }
        }
      } catch (error) {
        console.error('Error fetching request options:', error);
        // Keep defaults on error
      }
    };
    fetchRequestOptions();
  }, []);

  // Build category tree from flat data
  const categoryTree = useMemo(() => {
    if (!dbCategories.length) return [];
    
    const buildTree = (parentId = null) => {
      return dbCategories
        .filter(c => c.parent_id === parentId)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map(cat => ({
          ...cat,
          children: buildTree(cat.id)
        }));
    };
    
    return buildTree(null);
  }, [dbCategories]);

  // Filter categories by search
  const filteredCategories = useMemo(() => {
    if (!categorySearch.trim()) return categoryTree;
    
    const searchLower = categorySearch.toLowerCase();
    
    const filterTree = (categories) => {
      return categories.reduce((acc, cat) => {
        const matchesSelf = cat.name.toLowerCase().includes(searchLower);
        const filteredChildren = filterTree(cat.children || []);
        
        if (matchesSelf || filteredChildren.length > 0) {
          acc.push({
            ...cat,
            children: filteredChildren,
            highlighted: matchesSelf
          });
        }
        return acc;
      }, []);
    };
    
    return filterTree(categoryTree);
  }, [categoryTree, categorySearch]);

  // Build flat list of all categories for filter dropdown
  const allCategoriesFlat = useMemo(() => {
    const flatten = (categories, path = []) => {
      return categories.reduce((acc, cat) => {
        const currentPath = [...path, cat.name];
        acc.push({
          id: cat.id,
          name: cat.name,
          slug: cat.slug,
          fullPath: currentPath.join(' > '),
          depth: path.length
        });
        if (cat.children?.length) {
          acc.push(...flatten(cat.children, currentPath));
        }
        return acc;
      }, []);
    };
    return flatten(categoryTree);
  }, [categoryTree]);

  useEffect(() => {
    loadRequests();
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterCategory, filterPriceRange, filterTimeline, filterExpiry, sortBy, searchQuery, itemsPerPage]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      // Use native Supabase client to avoid fetch wrapper issues
      const { data, error } = await supabase
        .from('direct_requests')
        .select('*, profiles!direct_requests_client_id_fkey(full_name, avatar_url)')
        .eq('status', 'open')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error loading requests:', error);
      } else if (Array.isArray(data)) {
        setRequests(data);
      }
    } catch (error) {
      console.error('Error loading requests:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get price value for sorting (defined before useMemo that uses it)
  const getPriceValue = (priceRange) => {
    if (!priceRange) return 0;
    if (priceRange.startsWith('custom:')) {
      return parseFloat(priceRange.split(':')[1]) || 0;
    }
    switch (priceRange) {
      case '100-500': return 300;
      case '500-2000': return 1250;
      default: return 0;
    }
  };

  // Filter and sort requests
  const filteredAndSortedRequests = useMemo(() => {
    let result = [...requests];
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(r => 
        r.title?.toLowerCase().includes(query) ||
        r.description?.toLowerCase().includes(query)
      );
    }
    
    // Category filter
    if (filterCategory !== 'all') {
      result = result.filter(r => r.category === filterCategory);
    }
    
    // Price range filter
    if (filterPriceRange !== 'all') {
      result = result.filter(r => {
        const priceStr = r.price_range || '';
        if (filterPriceRange === 'under500') {
          return priceStr === '100-500' || (priceStr.startsWith('custom:') && parseFloat(priceStr.split(':')[1]) < 500);
        } else if (filterPriceRange === '500-2000') {
          return priceStr === '500-2000' || (priceStr.startsWith('custom:') && parseFloat(priceStr.split(':')[1]) >= 500 && parseFloat(priceStr.split(':')[1]) <= 2000);
        } else if (filterPriceRange === 'over2000') {
          return priceStr.startsWith('custom:') && parseFloat(priceStr.split(':')[1]) > 2000;
        }
        return true;
      });
    }
    
    // Timeline filter
    if (filterTimeline !== 'all') {
      result = result.filter(r => r.timeline === filterTimeline);
    }
    
    // Expiry filter (based on created_at date)
    if (filterExpiry !== 'all') {
      const now = new Date();
      result = result.filter(r => {
        const createdAt = new Date(r.created_at);
        const daysDiff = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24));
        if (filterExpiry === 'last7days') return daysDiff <= 7;
        if (filterExpiry === 'last30days') return daysDiff <= 30;
        if (filterExpiry === 'last90days') return daysDiff <= 90;
        return true;
      });
    }
    
    // Sorting
    result.sort((a, b) => {
      switch (sortBy) {
        case 'price_asc': {
          const priceA = getPriceValue(a.price_range);
          const priceB = getPriceValue(b.price_range);
          return priceA - priceB;
        }
        case 'price_desc': {
          const priceA = getPriceValue(a.price_range);
          const priceB = getPriceValue(b.price_range);
          return priceB - priceA;
        }
        case 'date_asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'date_desc':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });
    
    return result;
  }, [requests, filterCategory, filterPriceRange, filterTimeline, filterExpiry, sortBy, searchQuery, getPriceValue]);

  // Paginated results
  const paginatedRequests = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedRequests.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedRequests, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedRequests.length / itemsPerPage);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (attachments.length + files.length > 5) {
      toast.error('Massimo 5 allegati consentiti');
      return;
    }

    setUploading(true);
    const newAttachments = [];

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} supera i 5MB`);
        continue;
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      try {
        const { publicUrl } = await supabaseUpload('rarifex-uploads', fileName, file);
        newAttachments.push({
          name: file.name,
          url: publicUrl,
          type: file.type,
        });
      } catch (error) {
        console.error('Upload exception:', error);
        toast.error(`Errore upload ${file.name}`);
      }
    }

    setAttachments([...attachments, ...newAttachments]);
    setUploading(false);
    
    if (newAttachments.length > 0) {
      toast.success(`${newAttachments.length} file caricati`);
    }
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  // Handle category selection from tree
  const handleCategorySelect = (cat, path) => {
    setSelectedCategoryPath(path);
    setCategory(cat.slug);
    setCategorySearch('');
  };

  // Toggle category expansion
  const toggleCategoryExpand = (catId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(catId)) {
        newSet.delete(catId);
      } else {
        newSet.add(catId);
      }
      return newSet;
    });
  };

  // Get category label from selected path (for form)
  const getSelectedCategoryLabel = () => {
    if (selectedCategoryPath.length === 0) return '';
    return selectedCategoryPath.join(' > ');
  };

  // Render category tree item
  const renderCategoryItem = (cat, path = [], depth = 0) => {
    const currentPath = [...path, cat.name];
    const hasChildren = cat.children && cat.children.length > 0;
    const isExpanded = expandedCategories.has(cat.id);
    const isSelected = category === cat.slug;
    
    return (
      <div key={cat.id}>
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all ${
            isSelected 
              ? 'bg-[#008080] text-white' 
              : cat.highlighted 
                ? 'bg-yellow-100 hover:bg-yellow-200' 
                : 'hover:bg-slate-100'
          }`}
          style={{ paddingLeft: `${12 + depth * 16}px` }}
          onClick={() => {
            if (hasChildren) {
              toggleCategoryExpand(cat.id);
            }
            handleCategorySelect(cat, currentPath);
          }}
        >
          {hasChildren && (
            <ChevronDown 
              className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''} ${isSelected ? 'text-white' : 'text-slate-400'}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleCategoryExpand(cat.id);
              }}
            />
          )}
          {!hasChildren && <div className="w-4" />}
          <span className={`flex-1 text-sm ${isSelected ? 'font-medium' : ''}`}>
            {cat.name}
          </span>
          {isSelected && <Check className="w-4 h-4" />}
        </div>
        {hasChildren && isExpanded && (
          <div>
            {cat.children.map(child => renderCategoryItem(child, currentPath, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Devi essere autenticato per creare una richiesta');
      navigate('/login');
      return;
    }

    if (!title || !description || !category) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    if (priceRange === 'custom' && !customBudget) {
      toast.error('Inserisci il budget personalizzato');
      return;
    }

    if (timeline === 'custom' && !customDate) {
      toast.error('Inserisci la data di consegna desiderata');
      return;
    }

    setSubmitting(true);
    try {
      const requestData = {
        client_id: user.id,
        title,
        description,
        category: getSelectedCategoryLabel() || category,
        price_range: priceRange === 'custom' ? `custom:${customBudget}` : priceRange,
        timeline: timeline === 'custom' ? `custom:${customDate}` : timeline,
        attachments: attachments.map(a => a.url),
      };
      
      // Add gift fields if this is a gift request
      if (isGift) {
        if (giftEvent) requestData.gift_event = giftEvent;
        if (giftRecipient) requestData.gift_recipient = giftRecipient;
      }

      // Use XMLHttpRequest to bypass fetch body stream issues
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/requests/create`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new Error(xhr.responseText || 'Errore durante la creazione'));
          }
        };
        xhr.onerror = () => reject(new Error('Errore di rete'));
        xhr.send(JSON.stringify(requestData));
      });

      toast.success('Richiesta pubblicata! Gli artigiani la vedranno a breve.');
      setIsDialogOpen(false);
      resetForm();
      loadRequests();
      // Stay on /requests page - just reload the list
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.message || 'Errore durante la creazione della richiesta');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setCategory('');
    setSelectedCategoryPath([]);
    setCategorySearch('');
    setExpandedCategories(new Set());
    setPriceRange('');
    setCustomBudget('');
    setTimeline('');
    setCustomDate('');
    setAttachments([]);
    setStep(1);
    setIsGift(false);
    setGiftEvent('');
    setGiftRecipient('');
  };

  const openProposalDialog = (request) => {
    setSelectedRequest(request);
    setProposalMessage('');
    setProposalPrice('');
    // Pre-select the client's requested timeline (if valid, otherwise default)
    const clientTimeline = request.timeline || '';
    const isValidTimeline = timelineOptions.some(o => o.value === clientTimeline) || clientTimeline.startsWith('custom:');
    setProposalTimeline(isValidTimeline ? clientTimeline : (timelineOptions[0]?.value || '1month'));
    setIsProposalDialogOpen(true);
  };

  const handleSubmitProposal = async () => {
    if (!user) {
      toast.error('Devi essere autenticato');
      return;
    }

    if (!proposalMessage.trim()) {
      toast.error('Scrivi un messaggio per il cliente');
      return;
    }

    if (!proposalPrice || parseFloat(proposalPrice) <= 0) {
      toast.error('Inserisci un prezzo valido');
      return;
    }

    setSubmittingProposal(true);
    try {
      const proposalData = {
        request_id: selectedRequest.id,
        crafter_id: user.id,
        message: proposalMessage,
        proposed_price: parseFloat(proposalPrice),
        estimated_timeline: proposalTimeline,
        status: 'pending'
      };

      await supabasePost('proposals', proposalData, true);

      toast.success('Proposta inviata! Il cliente la riceverà nella sua dashboard.');
      setIsProposalDialogOpen(false);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Proposal error:', error);
      toast.error(error.message || 'Errore durante l\'invio della proposta');
    } finally {
      setSubmittingProposal(false);
    }
  };

  const canProceedStep1 = title.trim() && description.trim() && category;
  const canProceedStep2 = priceRange && timeline && 
    (priceRange !== 'exact' || customBudget) && 
    (timeline !== 'custom' || customDate);

  const getPriceLabel = (range) => {
    if (!range) return 'N/D';
    if (range.startsWith('custom:')) {
      return `€${range.split(':')[1]}`;
    }
    const found = budgetOptions.find(o => o.value === range);
    return found ? found.label : range;
  };

  const getTimelineLabel = (tl) => {
    if (!tl) return 'N/D';
    if (tl.startsWith('custom:')) {
      return `Data: ${tl.split(':')[1]}`;
    }
    const found = timelineOptions.find(o => o.value === tl);
    return found ? found.label : tl;
  };

  // Extract max budget from price_range for proposal validation
  const getMaxBudgetFromRange = (priceRange) => {
    if (!priceRange) return null;
    // Handle custom:XXX format
    if (priceRange.startsWith('custom:')) {
      return parseFloat(priceRange.split(':')[1]);
    }
    // Handle range format like "100-250", "500-1000", "5000+"
    if (priceRange.includes('-')) {
      const [, max] = priceRange.split('-');
      return parseFloat(max);
    }
    if (priceRange.endsWith('+')) {
      return parseFloat(priceRange.replace('+', ''));
    }
    return null;
  };

  // Get timeline sort order for comparison
  const getTimelineSortOrder = (timelineValue) => {
    if (!timelineValue) return 999;
    if (timelineValue.startsWith('custom:')) return 999; // Custom dates are flexible
    const found = timelineOptions.find(o => o.value === timelineValue);
    return found?.sort_order || 999;
  };

  // Check if proposed price exceeds client budget
  const isPriceOverBudget = () => {
    if (!selectedRequest?.price_range || !proposalPrice) return false;
    const maxBudget = getMaxBudgetFromRange(selectedRequest.price_range);
    if (maxBudget === null) return false;
    return parseFloat(proposalPrice) > maxBudget;
  };

  // Check if proposed timeline exceeds client request
  const isTimelineOverRequested = () => {
    if (!selectedRequest?.timeline || !proposalTimeline) return false;
    const requestedOrder = getTimelineSortOrder(selectedRequest.timeline);
    const proposedOrder = getTimelineSortOrder(proposalTimeline);
    return proposedOrder > requestedOrder;
  };

  // Get suggested price placeholder based on client budget
  const getPricePlaceholder = () => {
    if (!selectedRequest?.price_range) return 'Es: 350';
    const maxBudget = getMaxBudgetFromRange(selectedRequest.price_range);
    if (maxBudget) {
      return `Max budget: €${maxBudget}`;
    }
    return 'Es: 350';
  };

  const getCategoryLabel = (catValue) => {
    if (!catValue) return 'N/D';
    // New format: category path stored directly e.g. "Arte > Ceramica > Vasi"
    // Or just a slug/name
    return catValue;
  };

  const clearFilters = () => {
    setFilterCategory('all');
    setFilterPriceRange('all');
    setFilterTimeline('all');
    setFilterExpiry('all');
    setSortBy('date_desc');
    setSearchQuery('');
  };

  const hasActiveFilters = filterCategory !== 'all' || filterPriceRange !== 'all' || 
    filterTimeline !== 'all' || filterExpiry !== 'all' || searchQuery.trim();

  return (
    <>
      <Helmet>
        <title>Richieste Personalizzate | Artigianato Made in Italy | Rarifex</title>
        <meta name="description" content="Scopri le richieste di creazioni artigianali su misura. Trova clienti in cerca di prodotti unici fatti a mano da artigiani italiani. Pubblica la tua richiesta gratuitamente." />
        <meta name="keywords" content="richieste personalizzate, artigianato su misura, creazioni artigianali, made in italy, prodotti personalizzati, artigiani italiani" />
        <link rel="canonical" href="https://www.rarifex.com/requests" />
        <meta property="og:title" content="Richieste Personalizzate | Artigianato Made in Italy | Rarifex" />
        <meta property="og:description" content="Scopri le richieste di creazioni artigianali su misura. Trova clienti in cerca di prodotti unici fatti a mano da artigiani italiani." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/requests" />
      </Helmet>
      
      <div className="min-h-screen bg-slate-50" data-testid="requests-page">
        {/* SEO Hero Section */}
        <div className="bg-gradient-to-r from-[#008080] to-[#006666] text-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Richieste di Creazioni Artigianali
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-3xl mb-6">
              Hai un'idea ma non trovi quello che cerchi? Descrivi il tuo progetto e ricevi proposte 
              personalizzate dai migliori artigiani italiani. Gratuito e senza impegno.
            </p>
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Sparkles className="w-4 h-4" />
                <span>100% Made in Italy</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Clock className="w-4 h-4" />
                <span>Risposte rapide</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Gift className="w-4 h-4" />
                <span>Creazioni su misura</span>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Filter Bar */}
        <div className="sticky top-0 z-40 bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
            {/* Header Row */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-[#008080]">
                  {t('open_requests')}
                </h2>
                <p className="text-sm text-slate-500">
                  {filteredAndSortedRequests.length} richieste trovate
                </p>
              </div>

            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-[#008080] text-white hover:bg-[#008080]/90"
                  data-testid="create-request-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Pubblica Richiesta
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-request-dialog">
                <DialogHeader>
                  <DialogTitle className="text-2xl">Crea la tua Richiesta</DialogTitle>
                  <p className="text-sm text-slate-500 mt-1">Gli artigiani vedranno la tua richiesta e potranno inviarti proposte</p>
                </DialogHeader>

                {/* Progress indicator */}
                <div className="flex items-center justify-center gap-2 my-4">
                  {[1, 2, 3].map((s) => (
                    <div 
                      key={s} 
                      className={`w-3 h-3 rounded-full transition-all ${step >= s ? 'bg-[#008080]' : 'bg-slate-200'}`}
                    />
                  ))}
                </div>

                <form onSubmit={handleSubmit}>
                  <AnimatePresence mode="wait">
                    {/* STEP 1: Basic Info */}
                    {step === 1 && (
                      <motion.div
                        key="step1"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-4"
                      >
                        <div className="bg-[#008080]/5 rounded-lg p-4 mb-4">
                          <h3 className="font-semibold text-[#008080] mb-1">Passo 1: Cosa stai cercando?</h3>
                          <p className="text-sm text-slate-600">Descrivi la creazione dei tuoi sogni</p>
                        </div>

                        <div>
                          <Label htmlFor="title">Titolo della Richiesta *</Label>
                          <Input
                            id="title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Es: Borsa in pelle personalizzata per regalo"
                            className="mt-1"
                            data-testid="request-title-input"
                          />
                        </div>

                        <div>
                          <Label htmlFor="description">Descrivi cosa vuoi *</Label>
                          <Textarea
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Sii specifico: colori, dimensioni, materiali preferiti, occasione d'uso..."
                            rows={5}
                            className="mt-1"
                            data-testid="request-description-input"
                          />
                          <p className="text-xs text-slate-500 mt-1">{description.length}/500 caratteri</p>
                        </div>

                        {/* Category Selection - Dynamic Tree */}
                        <div className="space-y-3">
                          <Label>Categoria *</Label>
                          
                          {/* Search box */}
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              placeholder="Cerca categoria..."
                              value={categorySearch}
                              onChange={(e) => setCategorySearch(e.target.value)}
                              className="pl-10"
                              data-testid="request-category-search"
                            />
                          </div>

                          {/* Category Tree */}
                          <div className="max-h-64 overflow-y-auto border rounded-lg bg-white" data-testid="request-category-tree">
                            {categoryTree.length > 0 ? (
                              categoryTree
                                .filter(cat => {
                                  if (!categorySearch) return true;
                                  const searchLower = categorySearch.toLowerCase();
                                  const matchesName = cat.name.toLowerCase().includes(searchLower);
                                  const hasMatchingChild = cat.children?.some(
                                    child => child.name.toLowerCase().includes(searchLower) ||
                                      child.children?.some(gc => gc.name.toLowerCase().includes(searchLower))
                                  );
                                  return matchesName || hasMatchingChild;
                                })
                                .map(cat => renderCategoryItem(cat))
                            ) : (
                              <div className="p-4 text-center text-slate-500">
                                Caricamento categorie...
                              </div>
                            )}
                          </div>

                          {/* Selected category display */}
                          {category && (
                            <p className="text-sm text-[#008080] bg-[#008080]/10 px-3 py-2 rounded-lg">
                              Categoria selezionata: <strong>{getSelectedCategoryLabel() || category}</strong>
                            </p>
                          )}
                        </div>

                        {/* Gift Section - Collapsible */}
                        <div className="border border-rose-200 rounded-lg overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setIsGift(!isGift)}
                            className={`w-full flex items-center justify-between p-4 text-left transition-colors ${
                              isGift ? 'bg-rose-50' : 'hover:bg-slate-50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <Gift className={`w-5 h-5 ${isGift ? 'text-rose-500' : 'text-slate-400'}`} />
                              <div>
                                <span className={`font-medium ${isGift ? 'text-rose-700' : 'text-slate-700'}`}>
                                  È un regalo?
                                </span>
                                <p className="text-xs text-slate-500">Aiuta gli artigiani a capire meglio il contesto</p>
                              </div>
                            </div>
                            <div className={`w-10 h-6 rounded-full transition-colors relative ${isGift ? 'bg-rose-500' : 'bg-slate-200'}`}>
                              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${isGift ? 'translate-x-5' : 'translate-x-1'}`} />
                            </div>
                          </button>
                          
                          {isGift && (
                            <div className="p-4 border-t border-rose-200 bg-rose-50/50 space-y-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label className="text-sm text-slate-700">Per quale evento?</Label>
                                  <Select value={giftEvent} onValueChange={setGiftEvent}>
                                    <SelectTrigger className="mt-1" data-testid="gift-event-select">
                                      <SelectValue placeholder="Seleziona evento..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {GIFT_EVENTS.map(event => (
                                        <SelectItem key={event.value} value={event.value}>
                                          {event.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                
                                <div>
                                  <Label className="text-sm text-slate-700">Per chi è il regalo?</Label>
                                  <Select value={giftRecipient} onValueChange={setGiftRecipient}>
                                    <SelectTrigger className="mt-1" data-testid="gift-recipient-select">
                                      <SelectValue placeholder="Seleziona destinatario..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {GIFT_RECIPIENTS.map(rec => (
                                        <SelectItem key={rec.value} value={rec.value}>
                                          {rec.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <p className="text-xs text-slate-500">
                                <Heart className="w-3 h-3 inline mr-1 text-rose-400" />
                                Questi dettagli aiutano gli artigiani a creare qualcosa di speciale
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="flex justify-end pt-4">
                          <Button 
                            type="button" 
                            onClick={() => setStep(2)}
                            disabled={!canProceedStep1}
                            className="bg-[#008080] text-white"
                          >
                            Continua
                          </Button>
                        </div>
                      </motion.div>
                    )}

                    {/* STEP 2: Budget & Timeline */}
                    {step === 2 && (
                      <motion.div
                        key="step2"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-4"
                      >
                        <div className="bg-[#DAA520]/10 rounded-lg p-4 mb-4">
                          <h3 className="font-semibold text-[#DAA520] mb-1">Passo 2: Budget e Tempi</h3>
                          <p className="text-sm text-slate-600">Aiuta gli artigiani a capire le tue esigenze</p>
                        </div>

                        {/* Budget Selection */}
                        <div className="space-y-3">
                          <Label>Fascia di Budget *</Label>
                          <Select value={priceRange} onValueChange={(val) => {
                            setPriceRange(val);
                            if (val !== 'custom') setCustomBudget('');
                          }}>
                            <SelectTrigger data-testid="request-budget-select">
                              <SelectValue placeholder="Seleziona il tuo budget" />
                            </SelectTrigger>
                            <SelectContent>
                              {budgetOptions.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{opt.label}</span>
                                    <span className="text-xs text-slate-500">- {opt.description}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {priceRange === 'custom' && (
                            <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                              <Label htmlFor="customBudget">Indica il budget esatto (€)</Label>
                              <Input
                                id="customBudget"
                                type="number"
                                value={customBudget}
                                onChange={(e) => setCustomBudget(e.target.value)}
                                placeholder="Es: 750"
                                className="mt-1"
                                data-testid="request-custom-budget"
                              />
                            </div>
                          )}
                        </div>

                        {/* Timeline Selection */}
                        <div className="space-y-3">
                          <Label>Tempistiche *</Label>
                          <Select value={timeline} onValueChange={(val) => {
                            setTimeline(val);
                            if (val !== 'custom') setCustomDate('');
                          }}>
                            <SelectTrigger data-testid="request-timeline-select">
                              <SelectValue placeholder="Seleziona le tempistiche" />
                            </SelectTrigger>
                            <SelectContent>
                              {timelineOptions.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{opt.label}</span>
                                    <span className="text-xs text-slate-500">- {opt.description}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {timeline === 'custom' && (
                            <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                              <Label htmlFor="customDate">Data di consegna desiderata</Label>
                              <Input
                                id="customDate"
                                type="date"
                                value={customDate}
                                onChange={(e) => setCustomDate(e.target.value)}
                                className="mt-1"
                                min={new Date().toISOString().split('T')[0]}
                                data-testid="request-custom-date"
                              />
                            </div>
                          )}
                        </div>

                        <div className="flex justify-between pt-4">
                          <Button type="button" variant="outline" onClick={() => setStep(1)}>
                            Indietro
                          </Button>
                          <Button 
                            type="button" 
                            onClick={() => setStep(3)}
                            disabled={!canProceedStep2}
                            className="bg-[#008080] text-white"
                          >
                            Continua
                          </Button>
                        </div>
                      </motion.div>
                    )}

                    {/* STEP 3: Attachments & Review */}
                    {step === 3 && (
                      <motion.div
                        key="step3"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-4"
                      >
                        <div className="bg-green-50 rounded-lg p-4 mb-4">
                          <h3 className="font-semibold text-green-700 mb-1">Passo 3: Immagini di Riferimento</h3>
                          <p className="text-sm text-slate-600">Aggiungi foto o disegni per aiutare gli artigiani</p>
                        </div>

                        <div>
                          <Label>Allegati (opzionale)</Label>
                          <div className="mt-2 border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              onChange={handleFileUpload}
                              className="hidden"
                              id="file-upload"
                            />
                            <label 
                              htmlFor="file-upload"
                              className="cursor-pointer flex flex-col items-center"
                            >
                              {uploading ? (
                                <div className="animate-spin w-8 h-8 border-2 border-[#008080] border-t-transparent rounded-full" />
                              ) : (
                                <>
                                  <Upload className="w-8 h-8 text-slate-400 mb-2" />
                                  <p className="text-sm text-slate-600">Clicca per caricare immagini</p>
                                  <p className="text-xs text-slate-400 mt-1">PNG, JPG fino a 5MB (max 5 file)</p>
                                </>
                              )}
                            </label>
                          </div>

                          {attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {attachments.map((att, idx) => (
                                <div key={idx} className="relative group">
                                  <img 
                                    src={att.url} 
                                    alt={att.name}
                                    className="w-20 h-20 object-cover rounded-lg border"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeAttachment(idx)}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Summary */}
                        <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                          <h4 className="font-semibold">Riepilogo Richiesta</h4>
                          <div className="text-sm space-y-1">
                            <p><span className="text-slate-500">Titolo:</span> {title}</p>
                            <p><span className="text-slate-500">Categoria:</span> {getSelectedCategoryLabel() || category}</p>
                            <p><span className="text-slate-500">Budget:</span> {getPriceLabel(priceRange === 'custom' ? `custom:${customBudget}` : priceRange)}</p>
                            <p><span className="text-slate-500">Tempi:</span> {getTimelineLabel(timeline)}</p>
                            {isGift && (
                              <div className="mt-2 pt-2 border-t border-slate-200">
                                <p className="flex items-center gap-1">
                                  <Gift className="w-4 h-4 text-rose-500" />
                                  <span className="text-rose-600 font-medium">È un regalo</span>
                                </p>
                                {giftEvent && (
                                  <p><span className="text-slate-500">Evento:</span> {GIFT_EVENTS.find(e => e.value === giftEvent)?.label || giftEvent}</p>
                                )}
                                {giftRecipient && (
                                  <p><span className="text-slate-500">Per:</span> {GIFT_RECIPIENTS.find(r => r.value === giftRecipient)?.label || giftRecipient}</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex justify-between pt-4">
                          <Button type="button" variant="outline" onClick={() => setStep(2)}>
                            Indietro
                          </Button>
                          <Button 
                            type="submit"
                            disabled={submitting}
                            className="bg-[#008080] text-white"
                          >
                            {submitting ? 'Pubblicazione...' : 'Pubblica Richiesta'}
                          </Button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Filter Row */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Cerca richieste..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
                data-testid="search-requests"
              />
            </div>

            {/* Category Filter */}
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[180px] h-9" data-testid="filter-category">
                <Tag className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le categorie</SelectItem>
                {allCategoriesFlat.map((cat) => (
                  <SelectItem key={cat.slug} value={cat.slug}>
                    {cat.fullPath}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Price Filter */}
            <Select value={filterPriceRange} onValueChange={setFilterPriceRange}>
              <SelectTrigger className="w-[160px] h-9" data-testid="filter-price">
                <Coins className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Prezzo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i budget</SelectItem>
                <SelectItem value="under500">Fino a €500</SelectItem>
                <SelectItem value="500-2000">€500 - €2.000</SelectItem>
                <SelectItem value="over2000">Oltre €2.000</SelectItem>
              </SelectContent>
            </Select>

            {/* Timeline Filter */}
            <Select value={filterTimeline} onValueChange={setFilterTimeline}>
              <SelectTrigger className="w-[150px] h-9" data-testid="filter-timeline">
                <Clock className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Tempi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tempi</SelectItem>
                <SelectItem value="1-2weeks">1-2 settimane</SelectItem>
                <SelectItem value="1month">1 mese</SelectItem>
                <SelectItem value="urgent">Urgente</SelectItem>
              </SelectContent>
            </Select>

            {/* Expiry Filter */}
            <Select value={filterExpiry} onValueChange={setFilterExpiry}>
              <SelectTrigger className="w-[160px] h-9" data-testid="filter-expiry">
                <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Pubblicazione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Qualsiasi data</SelectItem>
                <SelectItem value="last7days">Ultimi 7 giorni</SelectItem>
                <SelectItem value="last30days">Ultimi 30 giorni</SelectItem>
                <SelectItem value="last90days">Ultimi 90 giorni</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px] h-9" data-testid="sort-by">
                <ArrowUpDown className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Ordina per" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date_desc">
                  <span className="flex items-center gap-2">Più recenti</span>
                </SelectItem>
                <SelectItem value="date_asc">
                  <span className="flex items-center gap-2">Meno recenti</span>
                </SelectItem>
                <SelectItem value="price_asc">
                  <span className="flex items-center gap-2">
                    <TrendingUp className="w-3 h-3" /> Prezzo crescente
                  </span>
                </SelectItem>
                <SelectItem value="price_desc">
                  <span className="flex items-center gap-2">
                    <TrendingDown className="w-3 h-3" /> Prezzo decrescente
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearFilters}
                className="text-slate-500 hover:text-slate-700"
              >
                <X className="w-4 h-4 mr-1" />
                Pulisci filtri
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Card key={i} className="p-5 animate-pulse">
                <div className="h-6 bg-slate-200 rounded w-3/4 mb-3" />
                <div className="h-4 bg-slate-200 rounded w-full mb-2" />
                <div className="h-4 bg-slate-200 rounded w-2/3" />
              </Card>
            ))}
          </div>
        ) : paginatedRequests.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {paginatedRequests.map((request, index) => (
                <motion.div
                  key={request.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                >
                  <Link to={`/request/${request.id}`}>
                    <Card 
                      className="p-5 hover:shadow-lg transition-all cursor-pointer group h-full"
                      data-testid={`request-card-${request.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-bold text-lg text-slate-800 group-hover:text-[#008080] transition-colors line-clamp-2">
                          {request.title}
                        </h3>
                        {request.timeline === 'urgent' && (
                          <Badge className="bg-red-100 text-red-700 flex-shrink-0 ml-2">
                            Urgente
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                        {request.description}
                      </p>

                      <div className="flex flex-wrap gap-2 mb-4">
                        <Badge variant="outline" className="text-[#008080] border-[#008080]/30">
                          <Tag className="w-3 h-3 mr-1" />
                          {getCategoryLabel(request.category)}
                        </Badge>
                        <Badge variant="outline" className="text-[#DAA520] border-[#DAA520]/30">
                          <Coins className="w-3 h-3 mr-1" />
                          {getPriceLabel(request.price_range)}
                        </Badge>
                        <Badge variant="outline" className="text-slate-600">
                          <Clock className="w-3 h-3 mr-1" />
                          {getTimelineLabel(request.timeline)}
                        </Badge>
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t">
                        <div className="flex items-center gap-2">
                          {request.profiles?.avatar_url ? (
                            <img 
                              src={request.profiles.avatar_url} 
                              alt="" 
                              className="w-7 h-7 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-[#008080]/20 flex items-center justify-center">
                              <span className="text-xs font-bold text-[#008080]">
                                {request.profiles?.full_name?.[0] || '?'}
                              </span>
                            </div>
                          )}
                          <span className="text-sm text-slate-600">
                            {request.profiles?.full_name || 'Utente'}
                          </span>
                        </div>
                        
                        {user && profile?.user_type === 'crafter' && request.client_id !== user.id && (
                          <Button 
                            size="sm"
                            className="bg-[#DAA520] text-white hover:bg-[#DAA520]/90"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openProposalDialog(request);
                            }}
                          >
                            <Sparkles className="w-4 h-4 mr-1" />
                            Proponi
                          </Button>
                        )}
                      </div>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 pt-6 border-t">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Mostra</span>
                  <Select value={itemsPerPage.toString()} onValueChange={(v) => setItemsPerPage(parseInt(v))}>
                    <SelectTrigger className="w-[80px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-slate-600">per pagina</span>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          className={currentPage === pageNum ? "bg-[#008080]" : ""}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>

                <span className="text-sm text-slate-600">
                  Pagina {currentPage} di {totalPages}
                </span>
              </div>
            )}
          </>
        ) : (
          <Card className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              {hasActiveFilters ? 'Nessuna richiesta trovata' : 'Nessuna richiesta aperta'}
            </h3>
            <p className="text-slate-500 mb-4">
              {hasActiveFilters 
                ? 'Prova a modificare i filtri di ricerca' 
                : 'Rarifex è appena nata: le prime richieste stanno arrivando. Pubblica la tua e ricevi proposte dagli artigiani.'
              }
            </p>
            {hasActiveFilters ? (
              <Button variant="outline" onClick={clearFilters}>
                Pulisci filtri
              </Button>
            ) : (
              <Button
                className="bg-[#008080] hover:bg-[#006666] text-white"
                onClick={() => navigate('/regalo-personalizzato')}
                data-testid="requests-empty-cta"
              >
                Pubblica la tua richiesta
              </Button>
            )}
          </Card>
        )}
      </div>

      {/* Proposal Dialog */}
      <Dialog open={isProposalDialogOpen} onOpenChange={setIsProposalDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Invia Proposta</DialogTitle>
            <p className="text-sm text-slate-500 mt-1">
              per "{selectedRequest?.title}"
            </p>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Il tuo messaggio per il cliente *</Label>
              <Textarea
                value={proposalMessage}
                onChange={(e) => setProposalMessage(e.target.value)}
                placeholder="Presentati e spiega come realizzeresti questa creazione..."
                rows={4}
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prezzo proposto (€) *</Label>
                <Input
                  type="number"
                  value={proposalPrice}
                  onChange={(e) => setProposalPrice(e.target.value)}
                  placeholder={getPricePlaceholder()}
                  className={`mt-1 ${isPriceOverBudget() ? 'border-amber-500' : ''}`}
                />
                {isPriceOverBudget() && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Il prezzo supera il budget del cliente. La proposta verrà comunque inviata.
                  </p>
                )}
              </div>
              <div>
                <Label>Tempi stimati *</Label>
                <Select value={proposalTimeline} onValueChange={setProposalTimeline}>
                  <SelectTrigger className={`mt-1 ${isTimelineOverRequested() ? 'border-amber-500' : ''}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timelineOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isTimelineOverRequested() && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    I tempi superano la richiesta del cliente. La proposta verrà comunque inviata.
                  </p>
                )}
              </div>
            </div>

            {/* Client request summary */}
            <div className="bg-slate-50 p-3 rounded-lg text-sm">
              <p className="font-medium text-slate-700 mb-1">Richiesta del cliente:</p>
              <p className="text-slate-600">
                Budget: <span className="font-medium">{getPriceLabel(selectedRequest?.price_range)}</span>
                {' • '}
                Tempi: <span className="font-medium">{getTimelineLabel(selectedRequest?.timeline)}</span>
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsProposalDialogOpen(false)}>
                Annulla
              </Button>
              <Button 
                onClick={handleSubmitProposal}
                disabled={submittingProposal}
                className="bg-[#DAA520] text-white"
              >
                {submittingProposal ? 'Invio...' : 'Invia Proposta'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
}
